/**
 * Frontend mirror of `supabase/functions/_shared/payment-policy.ts`.
 *
 * Keep this byte-faithful to the canonical backend source. Pure functions only —
 * no imports, no Supabase, no React. Used by the admin UI (PaymentsAdmin) and
 * the customer-facing booking page (Booking).
 *
 * Roadmap Phase 2 Payments — Deposits / full payment / refunds / cancellation
 * policy.
 */

export type PaymentType = "full" | "deposit";
export type DepositType = "percentage" | "fixed";

export interface PaymentPolicy {
  /** Whether checkout charges the full balance or only a deposit up front. */
  payment_type: PaymentType;
  /** A deposit is either a percentage of the total or a fixed major-unit amount. */
  deposit_type: DepositType;
  /** Percentage (0-100) or fixed amount, depending on deposit_type. */
  deposit_value: number;
  /** Free-text cancellation policy shown to customers before they book/pay. */
  cancellation_policy: string;
  /** Full refund is offered when cancelling at least this many hours ahead (0 = n/a). */
  refund_window_hours: number;
  /** Free-text refund terms (e.g. partial-refund rules within the window). */
  refund_policy: string;
}

export const DEFAULT_PAYMENT_POLICY: PaymentPolicy = {
  payment_type: "full",
  deposit_type: "percentage",
  deposit_value: 0,
  cancellation_policy: "",
  refund_window_hours: 0,
  refund_policy: "",
};

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(value, min), max);
}

function toNumber(value: unknown, fallback = 0): number {
  const n = typeof value === "string" ? Number(value) : (value as number);
  return Number.isFinite(n) ? (n as number) : fallback;
}

function toText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** Coerce arbitrary stored JSON into a complete, safe PaymentPolicy. */
export function normalisePaymentPolicy(raw: Partial<PaymentPolicy> | null | undefined): PaymentPolicy {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    payment_type: r.payment_type === "deposit" ? "deposit" : "full",
    deposit_type: r.deposit_type === "fixed" ? "fixed" : "percentage",
    deposit_value: Math.max(0, toNumber(r.deposit_value, 0)),
    cancellation_policy: toText(r.cancellation_policy),
    refund_window_hours: Math.max(0, Math.round(toNumber(r.refund_window_hours, 0))),
    refund_policy: toText(r.refund_policy),
  };
}

/**
 * The up-front deposit due for an invoice total under the org's policy. Returns 0
 * for a full-payment policy. A percentage is clamped to 0-100; a fixed amount (and
 * any percentage result) can never exceed the total.
 */
export function computeDepositAmount(total: number, policy: PaymentPolicy): number {
  if (!Number.isFinite(total) || total <= 0) return 0;
  if (policy.payment_type !== "deposit") return 0;

  const raw = policy.deposit_type === "percentage"
    ? total * (clamp(policy.deposit_value, 0, 100) / 100)
    : clamp(policy.deposit_value, 0, total);

  return Math.min(round2(raw), total);
}

export interface ChargeInput {
  total: number;
  amountPaid: number;
  amountRefunded?: number;
  /** Deposit due on this invoice (0 = full payment). Usually computeDepositAmount(...). */
  depositAmount?: number;
}

export interface ChargeResult {
  amount: number;
  kind: "deposit" | "balance";
}

/**
 * What a Checkout Session should charge right now: the deposit first (while the
 * net-paid amount is still below it), then the remaining balance. Refunds re-open
 * the outstanding balance. Never charges below zero.
 */
export function computeInvoiceChargeAmount(input: ChargeInput): ChargeResult {
  const total = toNumber(input.total, 0);
  const netPaid = toNumber(input.amountPaid, 0) - toNumber(input.amountRefunded, 0);
  const deposit = toNumber(input.depositAmount, 0);
  const outstanding = round2(total - netPaid);

  if (outstanding <= 0) return { amount: 0, kind: "balance" };

  if (deposit > 0 && netPaid < deposit) {
    return { amount: Math.min(round2(deposit - netPaid), outstanding), kind: "deposit" };
  }
  return { amount: outstanding, kind: "balance" };
}
